import React, { useEffect, useState } from "react";
import moment from "moment";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Container,
  Box,
  Button,
  Modal,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { styled } from "@mui/system";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Calendar from "../components/Calendar";
import { getTokenFromStorage } from "../services/helpers";
import {
  deleteBooking,
  getAdminBookings,
  updateBooking,
} from "../services/API";
import { ToastContainer } from "react-toastify";

const StyledTableCell = styled(TableCell)`
  && {
    text-align: center;
    padding: 8px;
    border: 1px solid rgba(224, 224, 224, 1);
    min-width: 100px;
    height: 60px;
  }
`;

const StyledHeadCell = styled(TableCell)`
  && {
    text-align: center;
    font-weight: bold;
    background-color: #f5f5f5;
    padding: 12px;
    position: sticky;
    top: 0;
    z-index: 1;
  }
`;

const BookingCard = styled(Box)`
  padding: 4px;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  }
`;

const timeSlots = Array.from({ length: 29 }, (_, i) => {
  const hour = Math.floor(i / 2) + 8;
  const minute = i % 2 === 0 ? "00" : "30";
  return `${hour.toString().padStart(2, "0")}:${minute}`;
});

const CourtContainer = styled(Box)`
  margin-bottom: 24px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const CourtHeader = styled(Box)`
  padding: 12px 16px;
  background-color: rgba(0, 0, 0, 0.02);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const Uzsakymai = () => {
  const [selectedDate, setSelectedDate] = useState(moment());
  const [bookings, setBookings] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedSport, setSelectedSport] = useState("all");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editBookingDate, setEditBookingDate] = useState("");
  const [editBookingStartTime, setEditBookingStartTime] = useState("");
  const [editBookingEndTime, setEditBookingEndTime] = useState("");
  const [editBookingCourt, setEditBookingCourt] = useState("");
  const [editBookingId, setEditBookingId] = useState("");

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  useEffect(() => {
    const fetchBookings = async () => {
      const token = getTokenFromStorage();
      const bookings = await getAdminBookings(token);
      setBookings(bookings);
      setLoaded(true);
    };
    fetchBookings();
  }, []);

  const filteredBookings = bookings
    .sort((a, b) => {
      if (a.sport !== b.sport) {
        return a.sport.localeCompare(b.sport);
      }
      if (a.courtId !== b.courtId) {
        return a.courtId - b.courtId;
      }
      return a.start.localeCompare(b.start);
    })
    .filter((booking) => {
      return (
        moment(booking.date).format("YYYY-MM-DD") ===
        selectedDate.format("YYYY-MM-DD")
      );
    })
    .filter((booking) => {
      if (selectedSport === "all") {
        return true;
      } else {
        return booking.sport === selectedSport;
      }
    });

  const handleSportFilter = (sport) => {
    setSelectedSport(sport);
  };

  const handleCancelBooking = async (bookingId) => {
    const confirmed = window.confirm(
      "Are you sure you want to cancel this booking?"
    );
    if (confirmed) {
      const token = getTokenFromStorage();
      await deleteBooking(token, bookingId);
      const updatedBookings = await getAdminBookings(token);
      setBookings(updatedBookings);
    }
  };

  const handleEditModalOpen = (booking) => {
    setSelectedBooking(booking);
    setEditBookingDate(moment(booking.date).format("YYYY-MM-DD"));
    setEditBookingStartTime(booking.start);
    setEditBookingEndTime(booking.end);
    setEditBookingCourt(booking.courtId);
    setEditBookingId(booking.id);
    setEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setSelectedBooking(null);
    setEditModalOpen(false);
  };

  const handleEditBooking = async () => {
    const token = getTokenFromStorage();
    const updatedBooking = {
      ...selectedBooking,
      courtId: editBookingCourt,
      date: editBookingDate,
      start: editBookingStartTime,
      end: editBookingEndTime,
    };
    await updateBooking(token, updatedBooking, editBookingId);
    const updatedBookings = await getAdminBookings(token);
    setBookings(updatedBookings);
    handleEditModalClose();
  };

  const getSportColor = (sport) => {
    switch (sport) {
      case "Padelis":
        return "#2196f3";
      case "Tinklinis":
        return "#4caf50";
      case "beachTennis":
        return "#ff9800";
      default:
        return "#9e9e9e";
    }
  };

  const sportConfigs = {
    Padelis: {
      title: "Padelis",
      courts: [1, 2, 3, 4, 5],
      color: "#bbdefb",
    },
    Tinklinis: {
      title: "Tinklinis",
      courts: [1, 2, 3],
      color: "#c8e6c9",
    },
    beachTennis: {
      title: "Paplūdimio tenisas",
      courts: [1, 2, 3],
      color: "#ffe0b2",
    },
  };

  const CourtSection = ({ courtId, bookings, sportConfig }) => {
    const courtBookings = bookings.filter((b) => b.courtId === courtId);

    return (
      <CourtContainer>
        <CourtHeader sx={{ backgroundColor: sportConfig.headerColor }}>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Aikštelė {courtId}
          </Typography>
        </CourtHeader>
        <TableContainer
          component={Paper}
          sx={{ backgroundColor: sportConfig.color }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <StyledHeadCell>Laikas</StyledHeadCell>
                <StyledHeadCell>Klientas</StyledHeadCell>
                <StyledHeadCell>Kontaktai</StyledHeadCell>
                <StyledHeadCell>Veiksmai</StyledHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {courtBookings.length === 0 ? (
                <TableRow>
                  <StyledTableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      Nėra rezervacijų šiai dienai
                    </Typography>
                  </StyledTableCell>
                </TableRow>
              ) : (
                courtBookings.map((booking) => (
                  <TableRow key={booking.id}>
                    <StyledTableCell sx={{ whiteSpace: "nowrap" }}>
                      {booking.start} - {booking.end}
                    </StyledTableCell>
                    <StyledTableCell>{booking.clientName}</StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2" color="text.secondary">
                        {booking.clientEmail}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {booking.clientPhone}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "center",
                        }}
                      >
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleCancelBooking(booking.id)}
                        >
                          Atšaukti
                        </Button>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() => handleEditModalOpen(booking)}
                        >
                          Redaguoti
                        </Button>
                      </Box>
                    </StyledTableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CourtContainer>
    );
  };

  const SportSection = ({ sport, bookings }) => {
    const sportConfig = sportConfigs[sport];

    if (bookings.length === 0) {
      return null;
    }

    const getBookingForTimeAndCourt = (time, courtId) => {
      return bookings.find((booking) => {
        const timeSlot = moment(time, "HH:mm");
        const start = moment(booking.start, "HH:mm");
        const end = moment(booking.end, "HH:mm");
        return (
          booking.courtId === courtId &&
          timeSlot.isBetween(start, end, null, "[)")
        );
      });
    };

    const renderBookingCard = (booking) => {
      if (!booking) return null;

      return (
        <BookingCard
          sx={{
            bgcolor: sportConfig.color,
            position: "relative",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.5,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: "bold" }}>
              {booking.start} - {booking.end}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {booking.clientName}
          </Typography>
          <Typography
            variant="caption"
            sx={{ display: "block", color: "text.secondary" }}
          >
            {booking.clientPhone}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5, mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleCancelBooking(booking.id);
              }}
              sx={{ minWidth: "auto", p: "2px 8px" }}
            >
              Atšaukti
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                handleEditModalOpen(booking);
              }}
              sx={{ minWidth: "auto", p: "2px 8px" }}
            >
              Redaguoti
            </Button>
          </Box>
        </BookingCard>
      );
    };

    return (
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">{sportConfig.title}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: 600,
              overflow: "auto",
              "& th": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <StyledHeadCell>Laikas</StyledHeadCell>
                  {sportConfig.courts.map((courtId) => (
                    <StyledHeadCell key={courtId}>
                      Aikštelė {courtId}
                    </StyledHeadCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {timeSlots.map((time) => (
                  <TableRow key={time}>
                    <StyledTableCell
                      sx={{ fontWeight: 500, backgroundColor: "#f5f5f5" }}
                    >
                      {time}
                    </StyledTableCell>
                    {sportConfig.courts.map((courtId) => (
                      <StyledTableCell key={`${time}-${courtId}`}>
                        {renderBookingCard(
                          getBookingForTimeAndCourt(time, courtId)
                        )}
                      </StyledTableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    );
  };

  if (!loaded) {
    return null;
  }

  return (
    <div>
      <Box sx={{ bgcolor: "#f5f5f5", py: 8, pt: "80px" }}>
        <Container maxWidth="lg">
          <ToastContainer
            position="top-center"
            autoClose={1500}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
          <Typography
            sx={{
              typography: { xs: "h4", sm: "h3" },
            }}
            align="center"
            gutterBottom
          >
            Rezervacijos
          </Typography>
          <Box
            sx={{
              mt: 4,
              mb: 4,
              display: "flex",
              alignContent: "center",
              justifyContent: "center",
            }}
          >
            <Calendar value={selectedDate} onChange={handleDateChange} />
          </Box>
          <Box sx={{ mt: 4, mb: 4 }}>
            <Typography
              sx={{
                typography: { xs: "h6", sm: "h5" },
              }}
              align="center"
              gutterBottom
            >
              Esami užsakymai - {selectedDate.format("YYYY-MM-DD")}
            </Typography>
          </Box>
          <Box
            sx={{
              mb: 4,
              display: "flex",
              justifyContent: "center",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Button
              variant={selectedSport === "all" ? "contained" : "outlined"}
              onClick={() => handleSportFilter("all")}
              sx={{ mr: { xs: 0, sm: 2 }, mb: { xs: 1, sm: 0 } }}
            >
              Visi
            </Button>
            <Button
              variant={selectedSport === "Padelis" ? "contained" : "outlined"}
              onClick={() => handleSportFilter("Padelis")}
              sx={{ mr: { xs: 0, sm: 2 }, mb: { xs: 1, sm: 0 } }}
            >
              Padelis
            </Button>
            <Button
              variant={selectedSport === "Tinklinis" ? "contained" : "outlined"}
              onClick={() => handleSportFilter("Tinklinis")}
              sx={{ mr: { xs: 0, sm: 2 }, mb: { xs: 1, sm: 0 } }}
            >
              Tinklinis
            </Button>
            <Button
              variant={
                selectedSport === "beachTennis" ? "contained" : "outlined"
              }
              onClick={() => handleSportFilter("beachTennis")}
            >
              Paplūdimio tenisas
            </Button>
          </Box>
          <Box sx={{ mt: 4 }}>
            {Object.keys(sportConfigs).map((sport) => {
              const sportBookings = filteredBookings.filter(
                (b) => b.sport === sport
              );
              if (selectedSport === "all" || selectedSport === sport) {
                return (
                  <SportSection
                    key={sport}
                    sport={sport}
                    bookings={sportBookings}
                  />
                );
              }
              return null;
            })}
          </Box>
          {filteredBookings.length === 0 && (
            <Typography
              variant="h6"
              align="center"
              color="text.secondary"
              sx={{ mt: 4, mb: 2 }}
            >
              Šiai dienai nėra aktyvių rezervacijų
            </Typography>
          )}
          <Modal open={editModalOpen} onClose={handleEditModalClose}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                bgcolor: "background.paper",
                boxShadow: 24,
                p: 4,
                borderRadius: 1,
              }}
            >
              <Typography
                sx={{
                  typography: { xs: "body1", sm: "h6" },
                  mb: 2,
                }}
                gutterBottom
              >
                Redaguoti užsakymą
              </Typography>
              <TextField
                label="Data"
                type="date"
                value={editBookingDate}
                onChange={(e) => setEditBookingDate(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Pradžia"
                type="time"
                value={editBookingStartTime}
                onChange={(e) => setEditBookingStartTime(e.target.value)}
                sx={{ mb: 2, ml: { xs: 0, sm: 2 } }}
              />
              <TextField
                label="Pabaiga"
                type="time"
                value={editBookingEndTime}
                onChange={(e) => setEditBookingEndTime(e.target.value)}
                sx={{ mb: 2, ml: { xs: 0, sm: 2 } }}
              />
              <TextField
                label="Aikštelės numeris"
                type="number"
                value={editBookingCourt}
                onChange={(e) => setEditBookingCourt(e.target.value)}
                sx={{ mb: 2, ml: { xs: 0, sm: 2 } }}
              />
              <Button
                variant="contained"
                onClick={handleEditBooking}
                sx={{ display: "flex" }}
              >
                Išsaugoti
              </Button>
            </Box>
          </Modal>
        </Container>
      </Box>
    </div>
  );
};

export default Uzsakymai;
